import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Stack,
  Textarea
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';

import { addErrorNotification } from '../../slice/notificationsSlice';
import Confirmation from '../confirmation/Confirmation';

import ColumnOptionsPropType from './ColumnOptionsPropType';

function EditRowModal(props) {
  const { isOpen, handleClose, onSubmit, columnOptions } = props;

  const [rowData, setRowData] = useState({ ...props.rowData });
  const prevRowData = { ...props.rowData };
  useEffect(() => setRowData({ ...props.rowData }), [isOpen, props.rowData]);

  const getData = () => {
    const result = { _id: rowData._id };
    for (const option of columnOptions) {
      if (option.editable(rowData, prevRowData)) {
        result[option.key] = rowData[option.key];
      }
    }
    return result;
  };

  const fileInputRef = useRef();

  const createInput = (options) => {
    const { key, type, values } = options;
    const value = rowData[key];
    if (value === undefined) {
      return;
    }

    const onChange = val => setRowData({ ...rowData, ...{ [key]: val } });

    const typeMap = {};
    typeMap.text = () => {
      return <Input borderColor='black' _hover={{ borderColor: 'blue.500' }} fontWeight={500} value={value} onChange={e => onChange(e.target.value)}/>;
    };
    typeMap.textarea = () => {
      return <Textarea borderColor='black' _hover={{ borderColor: 'blue.500' }} fontWeight={500} value={value} onChange={e => onChange(e.target.value)}/>;
    };
    typeMap.select = () => {
      return (
        <Select float='none' borderColor='black' _hover={{ borderColor: 'blue.500' }} position='relative' fontWeight={500} value={value} onChange={e => onChange(e.target.value)}>
          {values.map((v, i) => <option key={i} value={v}>{v}</option>)}
        </Select>
      );
    };
    typeMap.number = () => {
      return (
        <NumberInput borderColor='black' value={value || 0} onChange={val => onChange(Number(val))}>
          <NumberInputField />
        </NumberInput>
      );
    };
    typeMap.date = () => {
      return (
        <Input type='datetime-local' value={value && new Date(value).toISOString().slice(0, 16)} onChange={e => onChange(new Date(e.target.value).toString())}/>
      );
    };
    typeMap.image = () => {
      const acceptedImageTypes = new Set(['image/jpeg', 'image/png']);
      const handleImgUpload = e => {
        const img = e.target.files[0];
        if (!acceptedImageTypes.has(img.type)) {
          addErrorNotification('Image must be in .png or .jpg format', true);
          return;
        }
        const reader = new FileReader();
        let imgBase64;
        reader.onloadend = () => {
          imgBase64 = reader.result.substr(reader.result.indexOf(',') + 1);
          onChange(imgBase64);
        };
        reader.readAsDataURL(img);
      };
      return <input ref={fileInputRef} type='file' onChange={handleImgUpload}/>;
    };
    typeMap.textfile = () => {
      const handleFileUpload = e => {
        const file = e.target.files[0];
        if (!file.type.startsWith('text/')) {
          addErrorNotification('Provided file is not a text file', true);
          return;
        }
        const reader = new FileReader();
        let text;
        reader.onloadend = () => {
          text = reader.result;
          onChange(text);
        };
        reader.readAsText(file);
      };
      return <input ref={fileInputRef} type='file' onChange={handleFileUpload}/>;
    };

    return typeMap[type]();
  };

  const body = (
    <FormControl>
      <Stack spacing={6}>
      {
        columnOptions.filter(options => !React.isValidElement(rowData[options.key])).map((options, i) => {
          const { columnName, editable, key } = options;
          const isEditable = editable(rowData, prevRowData);
          return (
            <Flex alignItems='left' key={i} flexDir='column' align='center' justify='center'>
              <FormLabel><Heading color='#007bff' size='md'>{columnName}</Heading></FormLabel>
              {(isEditable) ? createInput(options) : rowData[key]}
              <Divider />
            </Flex>
          );
        })
      }
      </Stack>
    </FormControl>
  );

  return (
    <Confirmation
      body={body}
      header={'Edit'}
      isOpen={isOpen}
      handleClose={handleClose}
      handleAction={() => onSubmit(getData())}
      actionButtonBgColor='#007bff'
      actionButtonHover={{ bg: '#0069d9', color: 'whitesmoke' }}
    />
  );
}

EditRowModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  columnOptions: ColumnOptionsPropType,
  rowData: PropTypes.object.isRequired
};

export default EditRowModal;
